import type { ConnectorView } from '@tabel/shared/schemas/agent-client';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { CONNECTOR_ROLES, connectorsKey, listConnectors } from '../../lib/agent-api';
import { useAgentSocket } from '../../lib/agent-ws';
import { ApiError, reportClientError } from '../../lib/errors';
import { useRole } from '../../lib/role';
import { shouldReport } from './parts';

/*
 * Компьютеры страницы «Мои ИИ-клиенты» (006, T041, T042, T055): список `GET /api/agent/connectors` и выбранный компьютер
 * для карточки «Агенты на …».
 * - Роли — строка «Подключить и отключить свой компьютер» таблицы прав (`CONNECTOR_ROLES`); у остальных запроса нет.
 * - `403` (нет права «ИИ-агент») и `404` (функция 006 выключена) — разделов 006 нет, страница 005 как была. Другая
 *   ошибка — карточка «Компьютеры» с причиной и «Повторить».
 * - WebSocket `/api/agent/ws`: `connector` (компьютер в сети или нет, отключён, новый), `agents` (состояние агентов
 *   изменилось) и новое соединение после обрыва — список заново.
 * - Выбранный компьютер: выбранный человеком, если он не отключён; иначе первый в сети, иначе первый не отключённый.
 */

const SCREEN = 'MyAgents';

function pick(list: readonly ConnectorView[] | undefined, chosen: number | null): ConnectorView | null {
  if (!list) return null;
  const active = list.filter((c) => c.phase !== 'disabled');
  return active.find((c) => c.id === chosen) ?? active.find((c) => c.online) ?? active[0] ?? null;
}

export function useComputers() {
  const { role } = useRole();
  const eligible = (CONNECTOR_ROLES as readonly string[]).includes(role);
  const query = useQuery({ queryKey: connectorsKey(role), queryFn: listConnectors, enabled: eligible });
  const off = query.error instanceof ApiError && (query.error.status === 403 || query.error.status === 404);
  /** Разделы 006 есть на странице. */
  const available = eligible && !off;
  /** Список загружен: можно подключать компьютер. */
  const ready = available && query.data !== undefined;
  const [chosen, setChosen] = useState<number | null>(null);
  const refetch = query.refetch;

  // Выбор держится: новый компьютер в начале списка не уводит карточку агентов, пока человек сам не выберет его.
  const picked = available ? pick(query.data, chosen) : null;
  useEffect(() => {
    if (picked && picked.id !== chosen) setChosen(picked.id);
  }, [picked, chosen]);

  useEffect(() => {
    if (query.error && !off && shouldReport(query.error))
      reportClientError(SCREEN, 'load-connectors', query.error);
  }, [query.error, off]);

  useAgentSocket((e) => {
    if (e.op === 'connector' || e.op === 'agents' || (e.op === 'open' && e.reconnected)) void refetch();
  }, ready);

  return {
    available,
    ready,
    list: available ? query.data : undefined,
    error: available ? query.error : null,
    busy: query.isFetching,
    refetch,
    selected: picked,
    select: setChosen,
  };
}
